import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { testsAPI } from '../services/api';

function SubmissionDetails() {
    const { testId, submissionId } = useParams();
    const navigate = useNavigate();
    
    const [submission, setSubmission] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadSubmissionDetails();
    }, [testId, submissionId]);

    const loadSubmissionDetails = async () => {
        try {
            console.log('Loading submission details...', { testId, submissionId });
            const response = await testsAPI.getSubmissionDetails(testId, submissionId);
            console.log('Response:', response.data);
            
            setSubmission(response.data.submission);
            setAnswers(response.data.answers);
        } catch (err) {
            console.error('Error loading submission:', err);
            setError(err.response?.data?.error || 'Fehler beim Laden der Abgabe-Details');
        } finally {
            setLoading(false);
        }
    };

    const safeJsonParse = (data) => {
        if (!data) return null;
        if (typeof data === 'object') return data;
        try {
            return JSON.parse(data);
        } catch (e) {
            console.error('JSON parse error:', e, 'Data:', data);
            return data;
        }
    };

    const renderAnswer = (answer) => {
        const answerData = safeJsonParse(answer.answer_data);
        
        if (!answerData) return 'Keine Antwort';

        switch (answer.question_type) {
            case 'multiple_choice':
                return answerData;
            case 'free_text':
                return answerData;
            case 'true_false':
                return answerData ? 'Wahr' : 'Falsch';
            case 'matching':
                if (typeof answerData === 'object') {
                    return (
                        <div>
                            {Object.entries(answerData).map(([key, value]) => (
                                <div key={key} style={{ marginBottom: '0.25rem' }}>
                                    <strong>{key}:</strong> {value}
                                </div>
                            ))}
                        </div>
                    );
                }
                return JSON.stringify(answerData);
            default:
                return JSON.stringify(answerData);
        }
    };

    const renderCorrectAnswer = (answer) => {
        const correctAnswer = safeJsonParse(answer.correct_answer);
        
        if (!correctAnswer) return 'Nicht definiert';

        switch (answer.question_type) {
            case 'multiple_choice':
                return correctAnswer;
            case 'true_false':
                return correctAnswer ? 'Wahr' : 'Falsch';
            case 'matching':
                if (typeof correctAnswer === 'object') {
                    return (
                        <div>
                            {Object.entries(correctAnswer).map(([key, value]) => (
                                <div key={key} style={{ marginBottom: '0.25rem' }}>
                                    <strong>{key}:</strong> {value}
                                </div>
                            ))}
                        </div>
                    );
                }
                return JSON.stringify(correctAnswer);
            default:
                return JSON.stringify(correctAnswer);
        }
    };

    const getQuestionTypeName = (type) => {
        const names = {
            'multiple_choice': 'Multiple Choice',
            'true_false': 'Wahr/Falsch',
            'free_text': 'Freitext',
            'matching': 'Zuordnung'
        };
        return names[type] || type;
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>Lädt...</div>
            </div>
        );
    }

    if (error || !submission) {
        return (
            <div style={styles.container}>
                <div style={styles.error}>{error || 'Abgabe nicht gefunden'}</div>
                <button onClick={() => navigate(`/test/${testId}/submissions`)} style={styles.backButton}>
                    ← Zurück zu Abgaben
                </button>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1>Abgabe: {submission.student_name}</h1>
                <button onClick={() => navigate(`/test/${testId}/submissions`)} style={styles.backButton}>
                    ← Zurück
                </button>
            </div>

            <div style={styles.section}>
                <h2>{submission.test_title}</h2>
                <div style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                        <strong>Schüler:</strong>
                        <p>{submission.student_name}</p>
                    </div>
                    <div style={styles.infoItem}>
                        <strong>Abgegeben am:</strong>
                        <p>{new Date(submission.submitted_at).toLocaleString('de-DE')}</p>
                    </div>
                    {submission.score !== null && (
                        <div style={styles.infoItem}>
                            <strong>Punktzahl:</strong>
                            <p style={styles.scoreDisplay}>
                                {submission.score} / {submission.max_score}
                                {' '}({Math.round((submission.score / submission.max_score) * 100)}%)
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <div style={styles.section}>
                <h2>Antworten ({answers.length})</h2>
                {answers.length === 0 ? (
                    <p>Keine Antworten vorhanden</p>
                ) : (
                    answers.map((answer, index) => (
                        <div key={answer.id} style={styles.answerCard}>
                            <div style={styles.answerHeader}>
                                <h3>Frage {index + 1}</h3>
                                <div style={styles.answerMeta}>
                                    <span style={styles.badge}>{getQuestionTypeName(answer.question_type)}</span>
                                    {answer.is_correct !== null && (
                                        <span style={answer.is_correct ? styles.correctBadge : styles.incorrectBadge}>
                                            {answer.is_correct ? '✓ Richtig' : '✗ Falsch'}
                                        </span>
                                    )}
                                    <span style={styles.points}>
                                        {answer.points_awarded || 0} / {answer.points} Punkte
                                    </span>
                                </div>
                            </div>

                            <div style={styles.questionText}>
                                <strong>Frage:</strong> {answer.question_text}
                            </div>

                            <div style={styles.answerContent}>
                                <strong>Antwort des Schülers:</strong>
                                <div style={styles.studentAnswer}>
                                    {renderAnswer(answer)}
                                </div>
                            </div>

                            {answer.correct_answer && answer.question_type !== 'free_text' && (
                                <div style={styles.correctAnswer}>
                                    <strong>Richtige Antwort:</strong>
                                    <div>
                                        {renderCorrectAnswer(answer)}
                                    </div>
                                </div>
                            )}

                            {answer.question_type === 'free_text' && (
                                <div style={styles.manualGradeInfo}>
                                    💡 Diese Antwort muss manuell bewertet werden
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            <div style={styles.actions}>
                <button onClick={() => window.print()} style={styles.printButton}>
                    🖨️ Drucken
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: {
        padding: '2rem',
        maxWidth: '900px',
        margin: '0 auto',
    },
    loading: {
        textAlign: 'center',
        padding: '3rem',
        fontSize: '1.2rem',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem',
    },
    backButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    section: {
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1rem',
        marginTop: '1rem',
    },
    infoItem: {
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
    },
    scoreDisplay: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: '#28a745',
        marginTop: '0.5rem',
    },
    answerCard: {
        padding: '1.5rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '1rem',
        border: '1px solid #dee2e6',
    },
    answerHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '0.5rem',
    },
    answerMeta: {
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    badge: {
        padding: '0.25rem 0.75rem',
        backgroundColor: '#007bff',
        color: 'white',
        borderRadius: '12px',
        fontSize: '0.85rem',
    },
    correctBadge: {
        padding: '0.25rem 0.75rem',
        backgroundColor: '#28a745',
        color: 'white',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: 'bold',
    },
    incorrectBadge: {
        padding: '0.25rem 0.75rem',
        backgroundColor: '#dc3545',
        color: 'white',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: 'bold',
    },
    points: {
        color: '#666',
        fontSize: '0.9rem',
    },
    questionText: {
        marginBottom: '1rem',
        fontSize: '1.1rem',
    },
    answerContent: {
        marginBottom: '1rem',
    },
    studentAnswer: {
        padding: '1rem',
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        marginTop: '0.5rem',
        minHeight: '50px',
    },
    correctAnswer: {
        padding: '1rem',
        backgroundColor: '#d4edda',
        borderRadius: '4px',
        marginTop: '1rem',
    },
    manualGradeInfo: {
        padding: '1rem',
        backgroundColor: '#fff3cd',
        color: '#856404',
        borderRadius: '4px',
        marginTop: '1rem',
    },
    error: {
        backgroundColor: '#fee',
        color: '#c33',
        padding: '1rem',
        borderRadius: '4px',
        marginBottom: '1rem',
    },
    actions: {
        textAlign: 'center',
    },
    printButton: {
        padding: '1rem 2rem',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
};

export default SubmissionDetails;
